import { useState } from "react";
import { Subscriptions } from "../../../lib/apiTyped";
import { useAuth } from "../../../hooks/useAuth";
import { useOrgSnapshot } from "../../../hooks/useOrgSnapshot";
import { useResource } from "../../../lib/useResource";
import { useApiCall } from "../../../lib/useToastError";
import { useNotifications } from "../../../components/shared/NotificationsProvider";
import { PageHeader } from "../../../components/AppLayout";
import { Card } from "../../../components/shared/Card";
import { Button } from "../../../components/shared/Button";
import { Spinner } from "../../../components/shared/Spinner";
import { StatusBadge } from "../../../components/shared/StatusBadge";
import { PlansShowcase } from "../../../components/shared/PlansShowcase";
import { SubscriptionAgreementCheckbox } from "../../../components/shared/SubscriptionAgreementCheckbox";
import { fmtEur, fmtDate } from "../../../lib/format";

const LIVE = new Set(["active", "trialing", "past_due", "incomplete"]);

export function OwnerSubscription() {
  const { user } = useAuth();
  const { orgId, org } = useOrgSnapshot();
  const { data, loading, refetch } = useResource(
    () => (orgId ? Subscriptions.get(orgId) : Promise.resolve(null)),
    [orgId],
  );
  const contactContext = {
    orgId: orgId ?? undefined,
    orgName: (org?.name as string | undefined) ?? undefined,
    email: user?.email ?? undefined,
  };

  // Owner already accepted on a previous activation — checkbox skipped.
  const termsAlreadyAccepted = Boolean(
    (org as Record<string, unknown> | null)?.subscriptionTermsAcceptedAt,
  );
  const [termsAccepted, setTermsAccepted] = useState(false);
  const canActivate = termsAlreadyAccepted || termsAccepted;
  const { run } = useApiCall();
  const { confirm } = useNotifications();

  if (loading || !orgId) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  const sub = data?.subscription ?? null;
  const tier = data?.tier ?? null;
  const seatCount = data?.seatCount ?? 0;
  const status = data?.subscriptionStatus ?? null;
  const live = status ? LIVE.has(status) : false;

  async function activate() {
    if (!orgId) return;
    if (!termsAlreadyAccepted) {
      const accepted = await run(() => Subscriptions.acceptTerms(orgId));
      if (!accepted) return; // run() already surfaced the error toast
    }
    const result = await run(() => Subscriptions.checkout(orgId));
    if (result?.checkoutUrl) window.location.href = result.checkoutUrl;
  }
  async function openPortal() {
    if (!orgId) return;
    const result = await run(() => Subscriptions.portal(orgId));
    if (result?.portalUrl) window.location.href = result.portalUrl;
  }
  async function cancelSub() {
    if (!orgId) return;
    const ok = await confirm({
      title: "Cancel subscription",
      message:
        "It will stay active until the end of the current billing cycle. You won't be charged again.",
      confirmLabel: "Cancel at period end",
      variant: "danger",
    });
    if (!ok) return;
    await run(() => Subscriptions.cancel(orgId), {
      successMessage: "Cancellation scheduled.",
    });
    await refetch();
  }

  return (
    <>
      <PageHeader
        title="API subscription"
        subtitle="Recurring access to the Cowboy API. Pay per seat. Invite your team in the Team page once active."
      />

      {!data?.activeTier ? (
        <div className="space-y-6">
          <Card className="p-6">
            <h2 className="text-lg font-black text-ink mb-1">
              No plan yet
            </h2>
            <p className="text-sm text-ink-2">
              Choose the plan that fits and request it from the Cowboy team. We
              assign tier and seats before you activate the subscription.
            </p>
          </Card>
          <PlansShowcase contactContext={contactContext} />
        </div>
      ) : (
        <Card className="p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
                Plan
              </div>
              <div className="mt-1 text-2xl font-black text-ink">
                {tier?.name ?? data.activeTier}
              </div>
              <div className="text-sm text-ink-2 mt-0.5">
                {fmtEur(tier?.pricePerSeatEur ?? 0)} / seat / month · {seatCount} seats
              </div>
              <div className="text-sm text-ink-2 mt-0.5">
                Total: {fmtEur((tier?.pricePerSeatEur ?? 0) * seatCount)} / month
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <StatusBadge status={status ?? "—"} />
              {sub?.cancelAtPeriodEnd && (
                <span className="text-xs text-ink-3">cancels at period end</span>
              )}
              {sub?.currentPeriodEnd ? (
                <span className="text-xs text-ink-3">
                  next: {fmtDate(sub.currentPeriodEnd)}
                </span>
              ) : null}
            </div>
          </div>

          {!live && !termsAlreadyAccepted && (
            <div className="mt-6">
              <SubscriptionAgreementCheckbox
                accepted={termsAccepted}
                onChange={setTermsAccepted}
              />
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-6">
            {!live ? (
              <Button
                variant="primary"
                onClick={activate}
                disabled={!canActivate}
              >
                Activate &amp; choose payment method
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={openPortal}>
                  Manage payment method · invoices
                </Button>
                {!sub?.cancelAtPeriodEnd && (
                  <Button variant="link" onClick={cancelSub}>
                    Cancel at period end
                  </Button>
                )}
              </>
            )}
          </div>

          {status === "past_due" && (
            <p
              className="mt-4 text-xs"
              style={{ color: "#C61418" }}
            >
              Payment failed. Update your payment method from the portal to avoid suspension.
            </p>
          )}
        </Card>
      )}

      {data?.activeTier && (
        <div className="mt-10 space-y-3">
          <h2 className="text-base font-black text-ink">Other plans</h2>
          <p className="text-sm text-ink-2">
            Need more volume or something custom? Ask us for a change.
          </p>
          <PlansShowcase
            currentTierSlug={data.activeTier}
            contactContext={contactContext}
          />
        </div>
      )}
    </>
  );
}
